import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  DismissibleTag,
  FilterableMultiSelect,
  Loading,
} from "@carbon/react";
import Form from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import BasicInformationFileUpload from "./BasicInformationFileUpload";
import {
  basicInformationValidationSchema,
  BasicInformationFormFields,
} from "./basicInformationValidationSchema";
import {
  CompanyFileRequest,
  CompanyFileResponse,
  CompanyProfileRequest,
  CompanyProfileResponse,
  FileType,
} from "../../types";
import { useUpdateCompanyProfile } from "../../hooks/useCompany";
import { useFileInputUploader } from "../../hooks";
import { usePublicCountries } from "../../hooks/useCountries";
import { useUser, useNotification } from "../../context";
import "./basicInformationForm.scss";

interface BasicInformationFormProps {
  profile: CompanyProfileResponse;
  setIsDirty: (isDirty: boolean) => void;
}

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];

/**
 * The Basic Information wizard sub-form (`PUT /company/{id}/profile`), rebuilt on the Carbon form
 * primitives (Task 8, Profile/Company flow) in place of 543 lines of Tailwind + hand-rolled
 * `useState`. Split into three files to stay under the ~400-line budget: this component (fields +
 * save), `basicInformationValidationSchema.ts` (the extracted, mutation-proven Yup schema), and
 * `BasicInformationFileUpload.tsx` (one upload slot, used twice below).
 *
 * Two things this rewrite deliberately keeps, both load-bearing:
 *
 * 1. **The `{...profile, ...data}` spread in `onSubmit`.** Every wizard sub-form saves
 *    independently via its own `PUT /company/{id}/profile`, and the backend's
 *    `CompanyProfileUpdateRequest.taxId` is now `@NotBlank` (Task 2) — a save that sent only this
 *    step's own fields would drop every field owned by another step (addresses, contacts,
 *    certifications, ...) AND could 400 if `taxId` weren't carried along. `profile` (the GET DTO,
 *    taxId included) is spread first, this step's validated `data` second, so this step's fields
 *    win without erasing anything else.
 * 2. **`files` is a full-replacement array, not a delta.** `CompanyService#updateCompanyFiles`
 *    (backend) clears and re-adds the WHOLE list on every save — exactly like `addresses` — so
 *    `allFiles` here is seeded from `profile.files` and every change (a new catalog/brief upload,
 *    a removal) mutates that same array; `onSubmit` sends it whole, same as the pre-Carbon form's
 *    own `allFiles` state.
 *
 * One divergence from the pre-Carbon brief this task was handed: that brief describes this form as
 * having "logo / product-catalog / company-presentation" uploads, but the actual pre-Carbon
 * component (verified by reading it before this rewrite) only ever had two — products catalog
 * (`FileType.PRODUCT_CATALOG`) and company brief/presentation (`FileType.COMPANY_PRESENTATION`).
 * There is no `FileType.LOGO` in `types/file.type.ts`, and no logo upload control anywhere in this
 * file's git history. Both uploads that actually exist are migrated below; no logo upload was
 * invented.
 */
const BasicInformationForm = ({
  profile,
  setIsDirty,
}: BasicInformationFormProps) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { openNotification } = useNotification();

  const defaultValues: BasicInformationFormFields = useMemo(
    () => ({
      name: profile.name ?? "",
      commercialName: profile.commercialName ?? "",
      taxId: profile.taxId ?? "",
      website: profile.website ?? "",
      linkedinUrl: profile.linkedinUrl ?? "",
      annualRevenue:
        profile.annualRevenue === null || profile.annualRevenue === undefined
          ? ""
          : String(profile.annualRevenue),
    }),
    [profile]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<BasicInformationFormFields>({
    resolver: yupResolver(basicInformationValidationSchema),
    defaultValues,
  });

  // `profile` arrives from CompanyProfileForm.tsx's own fetch, already loaded by the time this
  // renders — but `useForm`'s `defaultValues` only ever seeds the form on the very first render.
  // Same re-sync precedent as EditSiteForm.tsx/EditUserForm.tsx. A successful save invalidates the
  // `useCompanyProfile` query (see `useUpdateCompanyProfile`), which eventually lands a fresh
  // `profile` here — this effect's `reset(defaultValues)` re-baselines the form to it, which is
  // also what clears React Hook Form's own `formState.isDirty` back to `false` after a save.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // React Hook Form's own dirty-tracking, forwarded to the wizard shell's shared `isDirty` flag
  // (the pre-Carbon form called `setIsDirty(true)` from its own `handleInputChange` on every
  // keystroke — this is the RHF-native equivalent, no per-field wiring needed). Deliberately
  // one-directional (only ever pushes `true`): the file-upload/-removal handlers below call
  // `setIsDirty(true)` directly too, since `allFiles` is plain component state RHF never sees —
  // if this effect also pushed `false` whenever `isDirty` (RHF's) is momentarily false, it would
  // clobber a file-driven dirty flag on the very next render. `setIsDirty(false)` is only ever set
  // explicitly, in the save mutation's own `onSuccess` below.
  useEffect(() => {
    if (isDirty) {
      setIsDirty(true);
    }
  }, [isDirty, setIsDirty]);

  const [allFiles, setAllFiles] = useState<
    (CompanyFileResponse | CompanyFileRequest)[]
  >(profile.files || []);
  // Files live outside React Hook Form, so RHF's `isDirty` never sees a catalog/brief upload or
  // removal — this flag tracks those so the Save button reflects real pending changes too.
  const [filesChanged, setFilesChanged] = useState(false);
  // The logo and supply countries live outside React Hook Form too, like the files above.
  const [logoKey, setLogoKey] = useState(profile.imageURL ?? "");
  const [logoName, setLogoName] = useState<string | null>(null);
  const [countryIds, setCountryIds] = useState<string[]>(
    () => (profile.supplyCountries ?? []).map((country) => country.id)
  );
  const { data: countries = [] } = usePublicCountries();
  const selectedCountries = countries.filter((country) =>
    countryIds.includes(country.id)
  );
  const markChanged = () => {
    setIsDirty(true);
    setFilesChanged(true);
  };
  useEffect(() => {
    setAllFiles(profile.files || []);
    setFilesChanged(false);
    setLogoKey(profile.imageURL ?? "");
    setLogoName(null);
    setCountryIds((profile.supplyCountries ?? []).map((country) => country.id));
  }, [profile.files, profile.imageURL, profile.supplyCountries]);

  // The file being uploaded right now, if any — mirrors the pre-Carbon form's own `fileType`
  // state: one `useFileInputUploader` instance is shared by both upload slots, so only one upload
  // can be in flight at a time, same as before.
  const [pendingUpload, setPendingUpload] = useState<{
    type: FileType;
    name: string;
  } | null>(null);

  const { mutate: updateCompanyProfile, isPending: isSaving } =
    useUpdateCompanyProfile(() => {
      openNotification(t("companyProfile.basicInformationSaved"));
      setIsDirty(false);
      setFilesChanged(false);
    });

  const { isPending: isUploading, mutate: uploadFile } = useFileInputUploader(
    (responseFileUrl) => {
      if (!responseFileUrl || !pendingUpload) return;
      setIsDirty(true);
      setFilesChanged(true);
      setAllFiles((prev) => [
        ...prev.filter((file) => file.type !== pendingUpload.type),
        {
          name: pendingUpload.name,
          description: "",
          type: pendingUpload.type,
          fileURL: responseFileUrl,
        },
      ]);
      setPendingUpload(null);
    }
  );

  const { isPending: isUploadingLogo, mutate: uploadLogo } =
    useFileInputUploader((responseFileUrl) => {
      if (!responseFileUrl) return;
      setLogoKey(responseFileUrl);
      markChanged();
    }, () => setLogoName(null));

  const handleLogoSelected = (file: File) => {
    if (!LOGO_TYPES.includes(file.type)) {
      openNotification(t("companyProfile.logoInvalidType"));
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      openNotification(t("companyProfile.fileTooLarge"));
      return;
    }
    setLogoName(file.name);
    uploadLogo(file);
  };

  const handleFileSelected = (fileType: FileType, file: File) => {
    // Same 50MB gate as the pre-Carbon form's own `handleSpecification` — including its exact
    // (untranslated-placeholder) notification copy; fixing that i18n key is out of this task's
    // scope.
    if (file.size > MAX_FILE_SIZE_BYTES) {
      openNotification(t("companyProfile.fileTooLarge"));
      return;
    }
    setIsDirty(true);
    setPendingUpload({ type: fileType, name: file.name });
    uploadFile(file);
  };

  const handleRemoveFile = (index: number) => {
    setIsDirty(true);
    setFilesChanged(true);
    setAllFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = (data: BasicInformationFormFields) => {
    updateCompanyProfile({
      ...profile,
      ...data,
      annualRevenue: data.annualRevenue === "" ? 0 : Number(data.annualRevenue),
      companyId: user?.company?.id,
      files: allFiles,
      imageURL: logoKey,
      supplyCountryIds: countryIds,
    } as CompanyProfileRequest);
  };

  const productsCatalogFile = allFiles.find(
    (file) => file.type === FileType.PRODUCT_CATALOG
  );
  const companyPresentationFile = allFiles.find(
    (file) => file.type === FileType.COMPANY_PRESENTATION
  );

  return (
    <div className="basic-information-form">
      <h3 className="basic-information-form__header">
        {t("companyProfile.basicInformation")}
      </h3>

      {isSaving ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          noValidate
          className="basic-information-form__form"
        >
          <div className="basic-information-form__grid">
            <TextField
              name="name"
              label={t("companyProfile.companyName")}
              control={control}
            />
            <TextField
              name="commercialName"
              label={t("companyProfile.commercialName")}
              control={control}
            />
            <TextField
              name="taxId"
              label={t("companyProfile.taxId")}
              control={control}
              disabled
            />
            <TextField
              name="website"
              label={t("companyProfile.website")}
              control={control}
              placeholder="https://www.example.com"
            />
            <TextField
              name="linkedinUrl"
              label={t("companyProfile.linkedinUrl")}
              control={control}
              placeholder="https://www.linkedin.com/company/example"
            />
            <TextField
              name="annualRevenue"
              label={t("companyProfile.annualRevenue")}
              control={control}
              type="number"
              min={0}
              placeholder="0"
            />
          </div>

          <FilterableMultiSelect
            id="supplyCountries"
            titleText={t("companyProfile.supplyCountries")}
            placeholder={t("cta.select")}
            items={countries}
            itemToString={(country) => country?.name ?? ""}
            selectedItems={selectedCountries}
            onChange={({ selectedItems }) => {
              setCountryIds((selectedItems ?? []).map((country) => country.id));
              markChanged();
            }}
          />

          <div className="basic-information-form__uploads">
            <BasicInformationFileUpload
              label={t("companyProfile.logo")}
              uploadLabel={
                isUploadingLogo ? t("cta.uploading") : t("companyProfile.uploadLogo")
              }
              accept={LOGO_TYPES}
              removeLabel={t("companyProfile.removeLogo")}
              fileName={
                logoKey || isUploadingLogo
                  ? logoName ?? t("companyProfile.currentLogo")
                  : null
              }
              isUploading={isUploadingLogo}
              onFileSelected={handleLogoSelected}
              onFileRemoved={() => {
                setLogoKey("");
                setLogoName(null);
                markChanged();
              }}
            />
            <BasicInformationFileUpload
              label={t("companyProfile.productsCatalog")}
              uploadLabel={
                isUploading && pendingUpload?.type === FileType.PRODUCT_CATALOG
                  ? t("cta.uploading")
                  : t("companyProfile.uploadProductsCatalog")
              }
              fileName={productsCatalogFile?.name || null}
              isUploading={
                isUploading && pendingUpload?.type === FileType.PRODUCT_CATALOG
              }
              onFileSelected={(file) =>
                handleFileSelected(FileType.PRODUCT_CATALOG, file)
              }
              onFileRemoved={() =>
                handleRemoveFile(allFiles.indexOf(productsCatalogFile!))
              }
            />
            <BasicInformationFileUpload
              label={t("companyProfile.brief")}
              uploadLabel={
                isUploading &&
                pendingUpload?.type === FileType.COMPANY_PRESENTATION
                  ? t("cta.uploading")
                  : t("companyProfile.uploadBrief")
              }
              fileName={companyPresentationFile?.name || null}
              isUploading={
                isUploading &&
                pendingUpload?.type === FileType.COMPANY_PRESENTATION
              }
              onFileSelected={(file) =>
                handleFileSelected(FileType.COMPANY_PRESENTATION, file)
              }
              onFileRemoved={() =>
                handleRemoveFile(allFiles.indexOf(companyPresentationFile!))
              }
            />
          </div>

          {allFiles.length > 0 && (
            <div className="basic-information-form__uploaded-files">
              <p className="basic-information-form__uploaded-files-title cds--label">
                {t("companyProfile.uploadedFiles")}
              </p>
              <div className="basic-information-form__uploaded-files-list">
                {allFiles.map((file, index) => (
                  <DismissibleTag
                    key={`${file.type}-${index}`}
                    type="blue"
                    text={file.name}
                    onClose={() => handleRemoveFile(index)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="basic-information-form__actions">
            <Button
              type="submit"
              disabled={isSaving || isUploadingLogo || (!isDirty && !filesChanged)}
            >
              {t("cta.save")}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default BasicInformationForm;
